/** Stage E — Recommendation Lifecycle timeline
 * Renders lifecycle_history[] entries for an opportunity / proposal / position.
 */
import { CheckCircle2, Circle, Clock, ArrowDown } from "lucide-react";

const STATE_LABELS = {
  discovered: "Discovered", analyzed: "Analyzed", reviewed: "Reviewed",
  prepared: "Prepared", approved: "Approved", executed: "Executed",
  managed: "Managed", closed: "Closed", reviewed_post: "Post-Review",
  learned: "Learned",
};

const STATE_ORDER = ["discovered", "analyzed", "reviewed", "prepared",
                      "approved", "executed", "managed", "closed",
                      "reviewed_post", "learned"];

function timeSince(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function LifecycleTimeline({ entries = [], currentState }) {
  const seen = new Set((entries || []).map(e => e.state));
  const merged = STATE_ORDER.map(s => {
    const hit = (entries || []).findLast?.(e => e.state === s) || (entries || []).filter(e => e.state === s).slice(-1)[0];
    return { state: s, entry: hit, done: seen.has(s), current: s === currentState };
  });

  if ((entries || []).length === 0) {
    return (
      <div className="border border-dashed border-border/40 p-4 text-xs text-muted-foreground text-center" data-testid="lifecycle-empty">
        No lifecycle history yet — actions taken on this item will populate here.
      </div>
    );
  }

  return (
    <ol className="space-y-2" data-testid="lifecycle-timeline">
      {merged.map(({ state, entry, done, current }) => (
        <li key={state} className={`flex items-start gap-3 ${done ? "" : "opacity-40"}`} data-testid={`lifecycle-step-${state}`}>
          <div className="pt-0.5 shrink-0">
            {current ? <Clock size={13} className="text-signal-blue"/>
              : done ? <CheckCircle2 size={13} className="pnl-up"/>
              : <Circle size={13} className="text-muted-foreground"/>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <div className={`text-xs ${current ? "text-signal-blue font-medium" : done ? "text-foreground" : "text-muted-foreground"}`}>
                {STATE_LABELS[state] || state}
              </div>
              {entry?.at && <div className="mono text-[10px] text-muted-foreground">{timeSince(entry.at)}</div>}
              {entry?.actor && <span className="chip !text-[9px] border-current text-muted-foreground">{entry.actor}</span>}
            </div>
            {entry?.note && <div className="text-[11px] text-muted-foreground mt-0.5">{entry.note}</div>}
          </div>
          {state !== "learned" && <ArrowDown size={10} className="text-border shrink-0 mt-0.5"/>}
        </li>
      ))}
    </ol>
  );
}
